
import { useEffect, useState } from 'react';

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/cc11bae6a5602038bff90874ecf81043.jpg"
          alt="子供たちが楽しくPCを学ぶ様子"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-pink-100/40 via-purple-100/40 to-yellow-100/40"></div>
      </div>

      {/* Floating Characters */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-4 sm:left-10 w-12 sm:w-20 h-12 sm:h-20 animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}>
          <div className="w-full h-full flex items-center justify-center text-3xl sm:text-6xl">🌟</div>
        </div>
        <div className="absolute top-32 sm:top-40 right-4 sm:right-20 w-10 sm:w-16 h-10 sm:h-16 animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}>
          <div className="w-full h-full flex items-center justify-center text-2xl sm:text-5xl">💻</div>
        </div>
        <div className="absolute bottom-40 left-4 sm:left-20 w-14 sm:w-24 h-14 sm:h-24 animate-bounce" style={{ animationDelay: '2s', animationDuration: '3.5s' }}>
          <div className="w-full h-full flex items-center justify-center text-4xl sm:text-7xl">🎨</div>
        </div>
        <div className="absolute top-60 right-8 sm:right-40 w-12 sm:w-20 h-12 sm:h-20 animate-bounce" style={{ animationDelay: '1.5s', animationDuration: '4.5s' }}>
          <div className="w-full h-full flex items-center justify-center text-3xl sm:text-6xl">🚀</div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/90 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-lg mb-6 sm:mb-8 animate-pulse">
            <span className="text-xl sm:text-2xl">👨‍👧</span>
            <span className="text-xs sm:text-sm font-bold text-pink-600">パパの愛から生まれたスクール</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-xl sm:text-4xl md:text-6xl font-black mb-4 sm:mb-6 leading-tight px-2">
            <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-yellow-500 bg-clip-text text-transparent block">
              プロのエンジニアじゃない、
            </span>
            <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-yellow-500 bg-clip-text text-transparent block">
              普通のパパが『娘の将来』を
            </span>
            <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-yellow-500 bg-clip-text text-transparent block">
              思って作った、
            </span>
            <span className="text-2xl sm:text-5xl md:text-7xl text-gray-800 mt-2 sm:mt-4 block">
              世界一やさしい
            </span>
            <span className="text-2xl sm:text-5xl md:text-7xl text-gray-800 block">
              PCスクール
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-sm sm:text-xl md:text-2xl text-gray-700 mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed font-medium px-2">
            大阪・梅田から全国へ。<br />
            非エンジニアでも、<br className="sm:hidden" />ITアレルギーでも大丈夫。<br />
            パパの愛から生まれた、<br className="sm:hidden" />置いてきぼりにしないカリキュラム。
          </p>

          {/* Character Image */}
          <div className={`mb-8 sm:mb-10 transform transition-all duration-1000 delay-300 ${isVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-0'} px-2`}>
            <div className="relative inline-block w-full max-w-2xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Heartwarming%20illustration%20of%20a%20father%20and%20young%20daughter%20happily%20looking%20at%20a%20computer%20screen%20together%2C%20cute%20kawaii%20anime%20style%2C%20pastel%20colors%2C%20simple%20clean%20background%20in%20soft%20pink%20and%20purple%20gradient%2C%20warm%20lighting%2C%20joyful%20expressions%2C%20educational%20atmosphere%2C%20child%20learning%20programming%2C%20loving%20parent%20teaching%20moment&width=600&height=400&seq=hero-char-001&orientation=landscape"
                alt="楽しそうにPCを操作する娘さんと見守るお父様"
                className="rounded-2xl sm:rounded-3xl shadow-2xl w-full mx-auto"
              />
              <div className="absolute -top-2 sm:-top-4 -right-2 sm:-right-4 bg-yellow-400 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-full font-bold text-xs sm:text-lg shadow-lg animate-bounce">
                実際の親子の様子！
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 transform transition-all duration-1000 delay-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} px-2`}>
            <a 
              href="#trial" 
              id="trial"
              className="group relative bg-gradient-to-r from-pink-500 to-purple-500 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full font-black text-base sm:text-xl shadow-2xl hover:shadow-pink-300 transform hover:scale-110 transition-all duration-300 whitespace-nowrap cursor-pointer overflow-hidden w-full sm:w-auto"
            >
              <span className="relative z-10 flex items-center justify-center space-x-2 sm:space-x-3">
                <span>🎉</span>
                <span>まずは無料体験</span>
                <span>🎉</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
            <div className="text-center">
              <p className="text-xs sm:text-sm text-gray-600 font-medium">
                ✅ 1分で完了！<br />
                ✅ 梅田校・オンラインから選べます
              </p>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="mt-8 sm:mt-12 flex flex-wrap items-center justify-center gap-3 sm:gap-8 px-2">
            <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-md">
              <span className="text-xl sm:text-2xl">💰</span>
              <span className="font-bold text-gray-700 text-sm sm:text-base whitespace-nowrap">月謝1万円</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-md">
              <span className="text-xl sm:text-2xl">💻</span>
              <span className="font-bold text-gray-700 text-sm sm:text-base whitespace-nowrap">PCレンタルOK</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-md">
              <span className="text-xl sm:text-2xl">🏠</span>
              <span className="font-bold text-gray-700 text-sm sm:text-base whitespace-nowrap">梅田＋オンライン</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce hidden sm:block">
        <div className="w-8 h-12 border-3 border-pink-400 rounded-full flex items-start justify-center p-2">
          <div className="w-2 h-3 bg-pink-400 rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}
